import Rocket from "./Rocket";
import common from "../common";
import {Emitter} from "pixi-particles";

export default class World extends PIXI.Container {

    static inRange(x, min, max) {
        return x >= min && x <= max;
    }

    constructor(path) {
        super();

        this.signals = {};
        this.signals.completed = new P3.utils.MiniSignal();

        this._sky = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._sky.width = common.STAGE_WIDTH;
        this._sky.height = common.STAGE_HEIGHT;
        this._sky.tint = 0x7bbce6;
        this.addChild(this._sky);

        // world state
        this._state = null;

        // world tween group
        this._tweens = new P3.utils.TWEEN.Group();

        // create layers
        this._bgHolder = new PIXI.Container();
        this.addChild(this._bgHolder);

        this._midBackHolder = new PIXI.Container();
        this.addChild(this._midBackHolder);

        this._bgStartHolder = new PIXI.Container();
        this.addChild(this._bgStartHolder);

        this._backCloudHolder = new PIXI.Container();
        this.addChild(this._backCloudHolder);

        this._mgHolder = new PIXI.Container();
        this.addChild(this._mgHolder);

        this._eventHolder = new PIXI.Container();
        this.addChild(this._eventHolder);

        this._midCloudHolder = new PIXI.Container();
        this.addChild(this._midCloudHolder);

        this._roadHolder = new PIXI.Container();
        this.addChild(this._roadHolder);

        this._particleHolder = new PIXI.Container();
        this.addChild(this._particleHolder);

        this._playerHolder = new PIXI.Container();
        this.addChild(this._playerHolder);

        this._frontCloudHolder = new PIXI.Container();
        this.addChild(this._frontCloudHolder);

        this._fgHolder = new PIXI.Container();
        this.addChild(this._fgHolder);

        this._jerryHolder = new PIXI.Container();
        this.addChild(this._jerryHolder);

        // create camera
        this._camera = new P3.NewCamera(
            new PIXI.Point(0, 0),
            new PIXI.Point(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5)
        );
        this._camera.zoom = 1.4;
        this._camera.bounds.height = -common.STAGE_HEIGHT * 0.25; // set lower bounds

        // add parallax layers
        this._camera.addLayer(new P3.CameraLayer(this._bgHolder, new PIXI.Point(0.3, 0.3)));
        this._camera.addLayer(new P3.CameraLayer(this._bgStartHolder, new PIXI.Point(0.4, 0.4)));
        this._camera.addLayer(new P3.CameraLayer(this._backCloudHolder, new PIXI.Point(0.2, 0.2)));
        this._camera.addLayer(new P3.CameraLayer(this._eventHolder, new PIXI.Point(0.5, 0.5)));
        this._camera.addLayer(new P3.CameraLayer(this._midBackHolder, new PIXI.Point(0.5, 0.5)));
        this._camera.addLayer(new P3.CameraLayer(this._mgHolder, new PIXI.Point(1, 1)));
        this._camera.addLayer(new P3.CameraLayer(this._midCloudHolder, new PIXI.Point(1, 1)));
        this._camera.addLayer(new P3.CameraLayer(this._roadHolder, new PIXI.Point(1, 1)));
        this._camera.addLayer(new P3.CameraLayer(this._particleHolder, new PIXI.Point(1, 1)));
        this._camera.addLayer(new P3.CameraLayer(this._playerHolder, new PIXI.Point(1, 1)));
        this._camera.addLayer(new P3.CameraLayer(this._frontCloudHolder, new PIXI.Point(1.5, 1.5)));
        this._camera.addLayer(new P3.CameraLayer(this._fgHolder, new PIXI.Point(1.5, 1.5)));
        this._camera.addLayer(new P3.CameraLayer(this._jerryHolder, new PIXI.Point(1.5, 1.5)));

        this._rocketSpeed = 800;

        this._skyGradient = new PIXI.Sprite(P3.assets.texture("bg_gradient"));
        this._skyGradient.width = common.STAGE_WIDTH * 25;
        this._skyGradient.height = common.STAGE_HEIGHT * 2;
        this._skyGradient.position.set(-common.STAGE_WIDTH, -2000);
        this._bgHolder.addChild(this._skyGradient);

        for (let i = 0; i < 12; i++) {
            const tile = new PIXI.Sprite(P3.assets.texture("bg_back_layer"));
            tile.y = 400;
            //tile.scale.set(1.5);
            tile.anchor.set(0, 1);
            tile.x = -common.STAGE_WIDTH + i * tile.width;
            this._bgHolder.addChild(tile);
        }

        const eventStep = 1600;
        this._hotAirBalloons = [];
        for (let i = 0; i < 5; i++) {
            const key = P3.utils.selectRandom([
                "asset_hot_air_baloon",
                "asset_hot_air_baloon1",
                "asset_hot_air_baloon2",
                "asset_hot_air_baloon3",
            ]);
            const balloon = new PIXI.Sprite(P3.assets.texture(key));
            balloon.scale.set(0.5);
            balloon.position.set(eventStep * i + P3.math.randomInt(-200, 400), P3.math.randomInt(-600, -1800));
            balloon.anchor.set(0.5);
            balloon.seen = false;
            this._eventHolder.addChild(balloon);
            this._hotAirBalloons.push(balloon);
        }

        this._planes = [];
        for (let i = 0; i < 5; i++) {
            const plane = new PIXI.Sprite(P3.assets.texture("asset_plane"));
            plane.position.set(eventStep * i * 2, P3.math.randomInt(-1100, -1800));
            plane.anchor.set(0.5);
            plane.scale.set(0.35);
            plane.scale.x = -plane.scale.x;
            plane.seen = false;
            this._eventHolder.addChild(plane);
            this._planes.push(plane);
        }

        this._birds = [];
        for (let i = 0; i < 5; i++) {
            const frames = [P3.assets.texture("asset_bird1"), P3.assets.texture("asset_bird2")];
            const bird = new PIXI.AnimatedSprite(frames, false);
            bird.position.set(eventStep * i + P3.math.randomInt(-200, 400), P3.math.randomInt(-200, -1200));
            bird.anchor.set(0.5);
            bird.scale.set(0.35);
            bird.scale.x = -bird.scale.x;
            bird.animationSpeed = 3;
            bird.seen = false;
            this._eventHolder.addChild(bird);
            this._birds.push(bird);
        }

        const cloudStep = 400;
        for (let i = 0; i < 16; i++) {
            const key = P3.utils.selectRandom([
                "asset_cloud_1",
                "asset_cloud_2",
                "asset_cloud_3",
                "asset_cloud_4",
            ]);
            const cloud = new PIXI.Sprite(P3.assets.texture(key));
            cloud.anchor.set(0.5);
            cloud.position.set(-common.STAGE_WIDTH + i * cloudStep + P3.math.randomInt(-200, 200), P3.math.randomInt(-400, -1100));

            cloud.scale.set(P3.math.random(0.5, 0.7));
            cloud.scale.x = Math.random() < 0.5 ? -cloud.scale.x : cloud.scale.x;
            cloud.alpha = 0.8;
            this._backCloudHolder.addChild(cloud);
        }

        // create mg tiles
        for (let i = 0; i < 12; i++) {
            const key = P3.utils.selectRandom([
                "bg_middle_layer_1",
                "bg_middle_layer_2",
                "bg_middle_layer_3",
                "bg_middle_layer_4"
            ]);
            const tile = new PIXI.Sprite(P3.assets.texture(key));
            tile.x = -common.STAGE_WIDTH + i * tile.width - 560;
            tile.y = 300;
            //tile.scale.set(2);
            tile.anchor.set(0, 1);

            this._midBackHolder.addChild(tile);
        }

        for (let i = 0; i < 16; i++) {
            const key = P3.utils.selectRandom([
                "asset_cloud_1",
                "asset_cloud_2",
                "asset_cloud_3",
                "asset_cloud_4",
            ]);
            const cloud = new PIXI.Sprite(P3.assets.texture(key));
            cloud.anchor.set(0.5);
            cloud.position.set(-common.STAGE_WIDTH + i * cloudStep + P3.math.randomInt(-200, 200), P3.math.randomInt(-800, -2400));

            cloud.scale.set(P3.math.random(0.7, 0.8));
            cloud.scale.x = Math.random() < 0.5 ? -cloud.scale.x : cloud.scale.x;
            cloud.alpha = 0.8;
            this._midCloudHolder.addChild(cloud);
        }


        /*
        for (let i = 0; i < 16; i++) {
            const key = P3.utils.selectRandom([
                "player_layer_bin",
                "player_layer_bush",
                "player_layer_bush1",
            ]);
            const cloud = new PIXI.Sprite(P3.assets.texture(key));
            cloud.anchor.set(0.5, 1);
            cloud.position.set(common.STAGE_WIDTH * 4 + i * cloudStep + P3.math.randomInt(-200, 200), -200);

            cloud.scale.set(P3.math.random(0.7, 1));
            cloud.scale.x = Math.random() < 0.5 ? -cloud.scale.x : cloud.scale.x;
            this._fgHolder.addChild(cloud);
        }
        
         */

        // fg tiles
        for (let i = 0; i < 12; i++) {
            const key = P3.utils.selectRandom([
                "bg_front_layer_1",
                "bg_front_layer_2",
                "bg_front_layer_3",
                "bg_front_layer_4",
                "bg_front_layer_5"
            ]);

            const tile = new PIXI.Sprite(P3.assets.texture(key));
            tile.x = -common.STAGE_WIDTH + i * tile.width;
            tile.y = 200;
            tile.anchor.set(0, 1);
            this._mgHolder.addChild(tile);
        }

        for (let i = 0; i < 16; i++) {
            const key = P3.utils.selectRandom([
                "asset_cloud_1",
                "asset_cloud_2",
                "asset_cloud_3",
                "asset_cloud_4",
            ]);
            const cloud = new PIXI.Sprite(P3.assets.texture(key));
            cloud.anchor.set(0.5);
            cloud.position.set(-common.STAGE_WIDTH + i * cloudStep + P3.math.randomInt(-200, 200), P3.math.randomInt(-800, -2400));

            cloud.scale.set(P3.math.random(1.2, 1.6));
            cloud.scale.x = Math.random() < 0.5 ? -cloud.scale.x : cloud.scale.x;
            cloud.alpha = 0.8;
            this._frontCloudHolder.addChild(cloud);
        }

        // this._midBackHolder.visible = false;
        this._mgHolder.visible = false;
        this._fgHolder.visible = false;

        // add some road tiles at the start
        for (let i = 0; i < 20; i++) {
            const tile = new PIXI.Sprite(P3.assets.texture("bg_road_2"));
            tile.x = -common.STAGE_WIDTH + i * tile.width;
            tile.y = 280;
            tile.anchor.set(0, 1);
            this._roadHolder.addChild(tile);
        }

        this._tom = new PIXI.spine.Spine(P3.assets.spineData("tom_side"));
        this._tom.state.setAnimation(0, "idle", true);
        this._tom.scale.set(0.2);
        this._tom.position.set(-300, 60);
        this._tom.autoUpdate = false;
        this._playerHolder.addChild(this._tom);

        this._ramp = new PIXI.Sprite(P3.assets.texture("asset_ramp"));
        this._ramp.anchor.set(0.5, 1);
        this._ramp.y = 100;
        this._playerHolder.addChild(this._ramp);

        // create rocket
        this._rocket = new Rocket();
        this._rocket.rotation = -Math.PI * 0.15;
        this._rocket.y = -60;
        this._playerHolder.addChild(this._rocket);

        this._smokeEmitters = [];
        this._fireEmitters = [];

        //    this._fireHolder = new PIXI.Container();

        this.launchEmitter = new Emitter(
            this._particleHolder,
            [P3.assets.texture("particle_smoke"), P3.assets.texture("particle_smoke1")],
            P3.assets.json("emitter_launchpad_idle_00")
        );

        this.launchEmitter.autoUpdate = false;
        this.launchEmitter.emit = true;

        for (let i = 1; i < 3; i++) {
            const bone = this._rocket.view.skeleton.findBone("exhaust_emitter_" + i);

            if (bone) {

                // create emitters
                const smokeEmitter = new Emitter(
                    this._particleHolder,
                    [P3.assets.texture("particle_smoke"), P3.assets.texture("particle_smoke1")],
                    P3.assets.json("emitter_smoke")
                );
                smokeEmitter.autoUpdate = false;
                smokeEmitter.emit = false;
                smokeEmitter.bone = bone;
                this._smokeEmitters.push(smokeEmitter);

                //     console.log("emitter_fire_0" + (i % 3 === 0) ? 0 : 1)
                let emitter = "emitter_fire_0";
                if (i % 3 === 0) {
                    emitter += "1";
                } else {
                    emitter += "0";
                }

                const fireEmitter = new Emitter(
                    this._rocket.emitterHolder,
                    [P3.assets.texture("particle_flame"), P3.assets.texture("particle_flame1"), P3.assets.texture("particle_flame2")],
                    P3.assets.json(emitter)
                );
                fireEmitter.autoUpdate = false;
                fireEmitter.emit = false;
                fireEmitter.bone = bone;
                this._fireEmitters.push(fireEmitter);

                // set starting emitter positions
                const local = this._playerHolder.toLocal(new PIXI.Point(bone.worldX, bone.worldY), this._rocket.view);
                smokeEmitter.updateOwnerPos(local.x, local.y);
                fireEmitter.updateOwnerPos(local.x, local.y);
                this.launchEmitter.updateSpawnPos(local.x, local.y);

            } else {
                break;
            }
        }

        this._camera.setTarget(this._rocket.position);

        this._path = path;
        this._pathLength = 0;

        // set path start to rocket position
        this._path[0].x = this._rocket.x;
        this._path[0].y = this._rocket.y;

        // estimate path length with euclidean distance
        for (let i = 1; i < this._path.length; i++) {

            const p0 = this._path[i - 1];
            const p1 = this._path[i];

            const v0 = new P3.math.Vector2(p0.x, p0.y);
            const v1 = new P3.math.Vector2(p1.x, p1.y);

            const distance = v0.subtract(v1).length;

            this._pathLength += distance;

        }

        this._table = new PIXI.Sprite(P3.assets.texture("asset_table"));
        this._table.anchor.set(0.5, 1);
        this._table.position.set(5, 50);
        this._jerryHolder.addChild(this._table);

        this._jerry = new PIXI.spine.Spine(P3.assets.spineData("jerry"));
        this._jerry.skeleton.setSlotsToSetupPose();
        this._jerry.state.setAnimation(0, "waiting", true);
        this._jerry.scale.set(0.4);
        this._jerry.autoUpdate = false;
        this._jerry.position.set(-130, 10);
        this._jerryHolder.addChild(this._jerry);

        this._crash = new PIXI.spine.Spine(P3.assets.spineData("vfx_clash"));
        // this._jerry.skeleton.setSlotsToSetupPose();
        // this._jerry.state.setAnimation(0, "idle2", true);
        this._crash.scale.set(0.4);
        this._crash.visible = false;
        this._crash.autoUpdate = false;
        // this._jerry.position.set(-130, 10);
        this._particleHolder.addChild(this._crash);

        this._tom.state.addListener({
            start: () => {

            },
            complete: () => {

            },
            event: (entry, event) => {
                if (event.data.name === "clash") {
                    this._crash.position.set(this._tom.x, this._tom.y);
                    this._crash.visible = true;
                    this._crash.state.setAnimation(0, "clash", false);
                }
            }
        }, this);

        const key = P3.utils.selectRandom([
            "player_layer_bin",
            "player_layer_bush",
            "player_layer_bush1",
        ]);
        const foreObject = new PIXI.Sprite(P3.assets.texture(key));
        foreObject.anchor.set(0.5, 1);
        foreObject.position.set(this._path[this._path.length - 1].x * 1.5 + P3.math.randomInt(-200, -100), 110);

        foreObject.scale.set(P3.math.random(0.7, 1));
        foreObject.scale.x = Math.random() < 0.5 ? -foreObject.scale.x : foreObject.scale.x;
        this._fgHolder.addChild(foreObject);

        this._ambientLoop = P3.audio.playSound('sfx_ambient_birds_00', {loop: true});
        this._rocketLoop = null;

    }

    begin() {
        this.setState(World.STATES.BEGIN);
    }

    /**
     * @param dt
     */
    update(dt) {

        this._tweens.update();

        this._rocket.update(dt);
        this._tom.update(dt);
        this._jerry.update(dt);
        this._crash.update(dt);

        for (let i = 0; i < this._smokeEmitters.length; i++) {
            const e = this._smokeEmitters[i];

            const local = this._playerHolder.toLocal(new PIXI.Point(e.bone.worldX, e.bone.worldY), this._rocket.view);
            e.updateOwnerPos(local.x, local.y);
            e.rotate(this._rocket.rotation * PIXI.RAD_TO_DEG);

            e.update(dt);
        }

        for (let i = 0; i < this._fireEmitters.length; i++) {
            const e = this._fireEmitters[i];

            // const local = this._playerHolder.toLocal(new PIXI.Point(e.bone.worldX, e.bone.worldY), this._rocket.view);
            //  e.updateOwnerPos(local.x, local.y);
            //   e.rotate(this._rocket.rotation * PIXI.RAD_TO_DEG);

            e.update(dt);
        }

        // update balloons
        for (let i = 0; i < this._hotAirBalloons.length; i++) {
            const b = this._hotAirBalloons[i];
            b.x += 50 * dt;
        }

        // update planes
        for (let i = 0; i < this._planes.length; i++) {
            const p = this._planes[i];
            p.x -= 400 * dt;
        }

        // update birds
        for (let i = 0; i < this._birds.length; i++) {
            const b = this._birds[i];
            b.update(dt);
            b.x -= 100 * dt;
        }

        this._camera.update();

        this.launchEmitter.update(dt);
        this._stateUpdate(dt);

        this._updateTiles();

    }

    _updateTiles() {

        const leftCloud = this._bgHolder.children[0];
        if (leftCloud.getGlobalPosition().x < -common.STAGE_WIDTH * 2) {
            // swap with end tile
            leftCloud.x = this._bgHolder.children[this._bgHolder.children.length - 1].x + this._bgHolder.children[this._bgHolder.children.length - 1].width;
            leftCloud.x = Math.round(leftCloud.x);
            leftCloud.y = Math.round(leftCloud.y);
            this._bgHolder.addChild(leftCloud);
        }

        let cloud = this._backCloudHolder.children[0];
        if (cloud.getGlobalPosition().x < -common.STAGE_WIDTH * 2) {
            // swap with end tile
            cloud.x = this._backCloudHolder.children[this._backCloudHolder.children.length - 1].x + this._backCloudHolder.children[this._backCloudHolder.children.length - 1].width + common.STAGE_WIDTH;
            cloud.x = Math.round(cloud.x);
            cloud.y = P3.math.randomInt(0, -1100);
            this._backCloudHolder.addChild(cloud);
        }

        cloud = this._midCloudHolder.children[0];
        if (cloud.getGlobalPosition().x < -common.STAGE_WIDTH * 2) {
            // swap with end tile
            cloud.x = this._midCloudHolder.children[this._midCloudHolder.children.length - 1].x + this._midCloudHolder.children[this._midCloudHolder.children.length - 1].width + common.STAGE_WIDTH;
            cloud.x = Math.round(cloud.x);
            cloud.y = P3.math.randomInt(-600, -2400);
            this._midCloudHolder.addChild(cloud);
        }

        cloud = this._frontCloudHolder.children[0];
        if (cloud.getGlobalPosition().x < -common.STAGE_WIDTH * 2) {
            // swap with end tile
            cloud.x = this._frontCloudHolder.children[this._frontCloudHolder.children.length - 1].x + this._frontCloudHolder.children[this._frontCloudHolder.children.length - 1].width + common.STAGE_WIDTH;
            cloud.x = Math.round(cloud.x);
            cloud.y = P3.math.randomInt(-800, -2400);
            this._frontCloudHolder.addChild(cloud);
        }

        const leftTile = this._mgHolder.children[0];
        if (leftTile.getGlobalPosition().x < -common.STAGE_WIDTH * 2) {
            // swap with end tile
            leftTile.x = this._mgHolder.children[this._mgHolder.children.length - 1].x + this._mgHolder.children[this._mgHolder.children.length - 1].width;
            leftTile.x = Math.round(leftTile.x);
            leftTile.y = Math.round(leftTile.y);
            this._mgHolder.addChild(leftTile);
        }

        const leftFront = this._midBackHolder.children[0];
        if (leftFront.getGlobalPosition().x < -common.STAGE_WIDTH * 2) {
            // swap with end tile
            leftFront.x = this._midBackHolder.children[this._midBackHolder.children.length - 1].x + this._midBackHolder.children[this._midBackHolder.children.length - 1].width;
            leftFront.x = Math.round(leftFront.x);
            leftFront.y = Math.round(leftFront.y);
            this._midBackHolder.addChild(leftFront);
        }

    }

    /**
     * @param state
     */
    setState(state) {
        this._state = state;
        this._stateBegin();
    }

    _stateBegin() {

        switch (this._state) {

            case World.STATES.BEGIN: {

                this.launchEmitter.emit = true;

                P3.audio.playSound("music_tom_to_rocket_00");


                // set animation
                this._tom.state.setAnimation(0, "running_to_the_rocket_and_jumping", false);
                this._tom.state.addAnimation(0, "sitting in the rocket_short", true, 0);

                // walk towards rocket
                new P3.utils.TWEEN.Tween(this._tom, this._tweens)
                    .to({x: 0}, 600)
                    .easing(P3.utils.TWEEN.Easing.Linear.None)
                    .onComplete(() => {
                        this._tom.position.set(0, 60);
                        this._rocket.addChildAt(this._tom, 0);


                        P3.utils.delayg(this._tweens, 1, () => {
                            this._jerry.state.setAnimation(0, "jumping_in_giggling_pushing_the_button", false);

                            const prelaunchSound = P3.audio.playSound("sfx_rocket_prelaunch_00");

                            P3.utils.delayg(this._tweens, 3, () => {

                                P3.audio.playSound("sfx_button_slam_00");
                                prelaunchSound.stop();

                                this.setState(World.STATES.FLY);
                            });
                        });

                    })
                    .start();

                break;
            }

            case World.STATES.FLY: {
                this.launchEmitter.emit = false;
                P3.audio.playMusic("music_flight_loop_0" + P3.math.randomInt(0, 5));

                // start emitters
                for (let i = 0; i < this._smokeEmitters.length; i++) {
                    const e = this._smokeEmitters[i];
                    e.emit = true;
                }

                P3.audio.playSound("sfx_rocket_launch_00");
                this._ambientLoop && this._ambientLoop.stop();
                this._rocketLoop = P3.audio.playSound('sfx_rocket_flight_loop_02', {loop: true});

                for (let i = 0; i < this._fireEmitters.length; i++) {
                    const e = this._fireEmitters[i];
                    e.emit = true;
                }

                // re-arrange
                // v = d / t
                // vt = d;
                // t = d / v
                const t = this._pathLength / this._rocketSpeed;

                // start bezier tween
                gsap.to(this._rocket, {
                    motionPath: {
                        path: this._path,
                        align: this._path,
                        alignOrigin: [0.5, 0.5],
                        useRadians: true,
                        autoRotate: true
                    },
                    duration: t,
                    ease: "none",
                    onComplete: () => {
                        //  console.log("path complete");
                        this._rocket.rotation = 0;
                        this.setState(World.STATES.FINISH_A);
                    }
                });

                // tween camera zoom
                new P3.utils.TWEEN.Tween(this._camera, this._tweens)
                    .to({
                        zoom: 1
                    }, 1600)
                    .easing(P3.utils.TWEEN.Easing.Cubic.InOut)
                    .start();

                // show / hide layers after a delay
                P3.utils.delayg(this._tweens, 3, () => {
                    this._fgHolder.visible = true;
                    this._midBackHolder.visible = true;
                    this._mgHolder.visible = true;
                    this._roadHolder.visible = false;
                    this._bgStartHolder.visible = false;
                });


                break;
            }

            case World.STATES.FINISH_A: {

                // tom falls out of rocket
                this._camera.setTarget(this._tom.position);
                this._tom.state.setAnimation(0, "in_flight_ejection", true);
                this._tom.position.set(this._rocket.x, this._rocket.y);
                this._playerHolder.addChildAt(this._tom, 0);

                this._rocketLoop.volume = 0.15;

                P3.audio.playSound("sfx_tom_fall_00");

                new P3.utils.TWEEN.Tween(this._rocket, this._tweens)
                    .to({x: this._rocket.x + common.STAGE_WIDTH}, 2600)
                    .easing(P3.utils.TWEEN.Easing.Linear.None)
                    .start();

                new P3.utils.TWEEN.Tween(this._tom, this._tweens)
                    .to({y: 120}, 5600)
                    .easing(P3.utils.TWEEN.Easing.Linear.None)
                    .onComplete(() => {

                        this._rocketLoop.stop();
                        P3.audio.playSound("sfx_crash_land_00");

                        P3.audio.playSound("music_tom_landing_0" + P3.math.randomInt(0, 1));
                        this._tom.state.setAnimation(0, "landing_legs_running2", false);
                        //this._tom.state.addAnimation(0, "dizzy", true, 0);

                        new P3.utils.TWEEN.Tween(this._tom, this._tweens)
                            .to({x: this._tom.x + 180}, 1100)
                            .delay(100)
                            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                            .onComplete(() => {
                                P3.utils.delayg(this._tweens, 3, () => {
                                    this.signals.completed.dispatch();
                                });
                            })
                            .start();

                    })
                    .start();

                P3.utils.delayg(this._tweens, 2, () => {
                    P3.audio.playSound("sfx_chute_open_00");

                    P3.audio.stopMusic();
                    P3.audio.playSound("music_parachute_release_0" + P3.math.randomInt(0, 1));
                    this._tom.state.setAnimation(0, "in_flight_parachute_release", false);
                    this._tom.state.addAnimation(0, P3.utils.selectRandom([
                        "in_flight_falling_down_on_parachute_happy",
                        "in_flight_falling_down_on_parachute_idle",
                        "in_flight_falling_down_on_parachute_looking_up"
                    ]), true, 0);
                });

                break;
            }

        }

    }

    /**
     * @param dt
     * @private
     */
    _stateUpdate(dt) {

        switch (this._state) {

            case World.STATES.BEGIN: {

                break;
            }
            case World.STATES.FLY: {

                const angle = this._rocket.rotation;

                // looking right, range -pi/3 -> pi / 3
                if (World.inRange(angle, -Math.PI / 3, Math.PI / 3)) {
                    if (this._rocket.state !== Rocket.STATES.RIGHT) {
                        this._rocket.state = Rocket.STATES.RIGHT;
                        this._tom.state.setAnimation(0, "in_flight_right", true);
                    }
                }
                // looking left
                else if (World.inRange(angle, Math.PI * 2, -Math.PI * 2 + Math.PI / 3) || World.inRange(angle, Math.PI * 2 - Math.PI / 3, Math.PI * 2)) {
                    if (this._rocket.state !== Rocket.STATES.LEFT) {
                        this._rocket.state = Rocket.STATES.LEFT;
                        this._tom.state.setAnimation(0, "in_flight_left", true);
                    }
                }
                // lookup up
                else if (World.inRange(angle, -Math.PI * 2 + Math.PI / 3, -Math.PI / 3)) {
                    if (this._rocket.state !== Rocket.STATES.UP) {
                        this._rocket.state = Rocket.STATES.UP;
                        this._tom.state.setAnimation(0, "in_flight_up", true);
                    }
                }
                // down
                else {
                    if (this._rocket.state !== Rocket.STATES.DOWN) {
                        this._rocket.state = Rocket.STATES.DOWN;
                        this._tom.state.setAnimation(0, "in_flight_down", true);
                    }
                }

                // handle sfx for things in sky
                for (let i = 0; i < this._hotAirBalloons.length; i++) {
                    const b = this._hotAirBalloons[i];
                    if (b.seen)
                        continue;
                    const globalPlayer = this._rocket.getGlobalPosition();
                    const global = b.getGlobalPosition();
                    const distance = new P3.Vector2(globalPlayer.x - global.x, globalPlayer.y - global.y).length;
                    if (distance < 600) {
                        P3.audio.playSound("sfx_flyby_hot_air_balloon_00");
                        b.seen = true;
                    }
                }

                for (let i = 0; i < this._planes.length; i++) {
                    const b = this._planes[i];
                    if (b.seen)
                        continue;
                    const globalPlayer = this._rocket.getGlobalPosition();
                    const global = b.getGlobalPosition();
                    const distance = new P3.Vector2(globalPlayer.x - global.x, globalPlayer.y - global.y).length;
                    if (distance < 600) {
                        P3.audio.playSound("sfx_flyby_jet_plane_flyby_00");
                        b.seen = true;
                    }
                }

                for (let i = 0; i < this._birds.length; i++) {
                    const b = this._birds[i];
                    if (b.seen)
                        continue;
                    const globalPlayer = this._rocket.getGlobalPosition();
                    const global = b.getGlobalPosition();
                    const distance = new P3.Vector2(globalPlayer.x - global.x, globalPlayer.y - global.y).length;
                    if (distance < 600) {
                        b.seen = true;
                        if (Math.random() < 0.5)
                            P3.audio.playSound("sfx_flyby_crow_0" + P3.math.randomInt(0, 1));
                    }
                }

                break;
            }
            case World.STATES.FINISH_A: {
                break;
            }

        }
    }
}

World.STATES = {};
World.STATES.BEGIN = "BEGIN";
World.STATES.FLY = "FLY";
World.STATES.FINISH_A = "FINISH_A";
World.STATES.ENDED = "ENDED";